import React, { useEffect, useState } from "react";
import PageHeader from "../../../components/common/PageHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPlus,
    faSort,
    faSortUp,
    faSortDown,
    faPen,
    faTimes,
    faCheck,
} from "@fortawesome/free-solid-svg-icons";

import {
    getPaymentTermsList,
    createPaymentTerm,
    updatePaymentTerm,
} from "../api/paymentTermsService";

import { PaymentTerm } from "../types";
import { toast } from "react-toastify";

import "../styles/PaymentTermsList.css";

const PaymentTermsList: React.FC = () => {
    const [list, setList] = useState<PaymentTerm[]>([]);
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [pageSize, setPageSize] = useState<number | null>(null);
    const [nextPage, setNextPage] = useState<string | null>(null);
    const [prevPage, setPrevPage] = useState<string | null>(null);

    const [filters, setFilters] = useState({
        name: "",
        description: "",
        is_active: "",
    });

    const [ordering, setOrdering] = useState("");
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [isFetching, setIsFetching] = useState(false);

    const [editingId, setEditingId] = useState<number | "new" | null>(null);
    const [form, setForm] = useState({
        name: "",
        description: "",
        is_active: true,
    });

    const getSortIcon = (field: string) => {
        if (ordering === field) return faSortUp;
        if (ordering === `-${field}`) return faSortDown;
        return faSort;
    };

    const toggleOrdering = (field: string) => {
        setOrdering((prev) => {
            if (prev === field) return `-${field}`;
            if (prev === `-${field}`) return "";
            return field;
        });
    };

    useEffect(() => {
        fetchList(page);
    }, [page, ordering, filters]);

    const fetchList = async (pageNo: number) => {
        if (!isInitialLoad) setIsFetching(true);

        try {
            const params: any = {
                page: pageNo,
                name__icontains: filters.name,
                description__icontains: filters.description,
                ordering,
            };

            // ✅ Only send is_active if selected
            if (filters.is_active !== "") {
                params.is_active = filters.is_active;
            }

            const res = await getPaymentTermsList(pageNo, params);

            setList(res.data.results || []);
            setTotalCount(res.data.count || 0);
            setNextPage(res.data.next);
            setPrevPage(res.data.previous);

            if (pageSize === null && res.data.results?.length) {
                setPageSize(res.data.results.length);
            }
        } finally {
            setIsFetching(false);
            setIsInitialLoad(false);
        }
    };

    const totalPages =
        pageSize && totalCount ? Math.ceil(totalCount / pageSize) : 1;

    const handleAddNew = () => {
        setEditingId("new");
        setForm({
            name: "",
            description: "",
            is_active: true,
        });
    };

    const handleEdit = (item: PaymentTerm) => {
        setEditingId(item.id);
        setForm({
            name: item.name,
            description: item.description,
            is_active: item.is_active,
        });
    };
    const capitalizeMessage = (msg: string) => {
        return msg
            .split(" ")
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };
    const handleSave = async () => {
        try {
            if (!form.name.trim()) {
                toast.error("Name is required");
                return;
            }

            if (editingId === "new") {
                await createPaymentTerm(form);
                toast.success("Payment Term created successfully");
            } else if (editingId) {
                await updatePaymentTerm(editingId, form);
                toast.success("Payment Term updated successfully");
            }

            setEditingId(null);
            fetchList(page);
        } catch (error: any) {
            if (error.response?.data) {
                const errors = error.response.data;

                if (errors.name?.length) {
                    toast.error(capitalizeMessage(errors.name[0]));
                } else {
                    const firstError = Object.values(errors)[0];
                    if (Array.isArray(firstError)) {
                        toast.error(capitalizeMessage(firstError[0]));
                    } else {
                        toast.error("Something Went Wrong");
                    }
                }
            } else {
                toast.error("Something Went Wrong");
            }
        }
    };

    const handleCancel = () => {
        setEditingId(null);
    };

    return (
        <>
            <PageHeader
                title="Payment Terms"
                breadcrumbs={[
                    { label: "Home", path: "/dashboard" },
                    { label: "Masters" },
                    { label: "Payment Terms" },
                ]}
            />

            <div className="container mt-4">
                <div className="d-flex justify-content-end mb-3">
                    <button className="btn btn-primary" onClick={handleAddNew}>
                        <FontAwesomeIcon icon={faPlus} /> Add Payment Term
                    </button>
                </div>

                <div className="hr-card">
                    {isInitialLoad && <div className="skeleton-table" />}
                    {isFetching && (
                        <div className="table-loading-overlay">
                            <span className="spinner-border spinner-border-sm" />
                        </div>
                    )}

                    {!isInitialLoad && (
                        <>
                            <table className="table hr-table">
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: "center" }}>Action</th>
                                        <th
                                            className="sortable"
                                            onClick={() => toggleOrdering("name")}
                                        >
                                            Name <FontAwesomeIcon icon={getSortIcon("name")} />
                                        </th>
                                        <th
                                            className="sortable"
                                            onClick={() => toggleOrdering("description")}
                                        >
                                            Description{" "}
                                            <FontAwesomeIcon icon={getSortIcon("description")} />
                                        </th>
                                        <th>Status</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {/* ADD NEW ROW */}
                                    {editingId === "new" && (
                                        <tr>
                                            <td className="text-center">
                                                <button
                                                    className="btn btn-sm btn-primary"
                                                    onClick={handleSave}
                                                    title="Save"
                                                >
                                                    <FontAwesomeIcon icon={faCheck} />
                                                </button>

                                                <button
                                                    className="btn btn-sm btn-secondary ms-2"
                                                    onClick={handleCancel}
                                                    title="Cancel"
                                                >
                                                    <FontAwesomeIcon icon={faTimes} />
                                                </button>
                                            </td>
                                            <td>
                                                <input
                                                    className="form-control"
                                                    value={form.name}
                                                    onChange={(e) =>
                                                        setForm({ ...form, name: e.target.value })
                                                    }
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            handleSave();
                                                        }
                                                    }}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    className="form-control"
                                                    value={form.description}
                                                    onChange={(e) =>
                                                        setForm({ ...form, description: e.target.value })
                                                    }
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            handleSave();
                                                        }
                                                    }}
                                                />
                                            </td>
                                            <td>
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={filters.is_active}
                                                    onChange={(e) =>
                                                        setFilters({
                                                            ...filters,
                                                            is_active: e.target.value,
                                                        })
                                                    }
                                                >
                                                  
                                                    <option value="true">Active</option>
                                                    <option value="false">Inactive</option>
                                                </select>
                                            </td>
                                        </tr>
                                    )}

                                    {list.map((item) => (
                                        <tr key={item.id}>
                                            {editingId === item.id ? (
                                                <>
                                                    <td className="text-center">
                                                        <button
  className="btn btn-sm btn-primary"
  onClick={handleSave}
  title="Save"
>
  <FontAwesomeIcon icon={faCheck} />
</button>

<button
  className="btn btn-sm btn-secondary ms-2"
  onClick={handleCancel}
  title="Cancel"
>
  <FontAwesomeIcon icon={faTimes} />
</button>
                                                    </td>

                                                    <td>
                                                        <input
                                                            className="form-control"
                                                            value={form.name}
                                                            onChange={(e) =>
                                                                setForm({ ...form, name: e.target.value })
                                                            }
                                                        />
                                                    </td>

                                                    <td>
                                                        <input
                                                            className="form-control"
                                                            value={form.description}
                                                            onChange={(e) =>
                                                                setForm({ ...form, description: e.target.value })
                                                            }
                                                        />
                                                    </td>

                                                    <td>
                                                        <select
                                                            className="form-select"
                                                            value={form.is_active ? "true" : "false"}
                                                            onChange={(e) =>
                                                                setForm({
                                                                    ...form,
                                                                    is_active: e.target.value === "true",
                                                                })
                                                            }
                                                        >
                                                            <option value="true">Active</option>
                                                            <option value="false">Inactive</option>
                                                        </select>
                                                    </td>
                                                </>
                                            ) : (
                                                <>
                                                    <td className="text-center">
                                                        <button
                                                            className="btn btn-sm btn-primary"
                                                            onClick={() => handleEdit(item)}
                                                        >
                                                            <FontAwesomeIcon icon={faPen} />
                                                        </button>
                                                    </td>

                                                    <td>{item.name}</td>
                                                    <td>{item.description || "-"}</td>
                                                    <td>
                                                        <span
                                                            className={`status-badge ${item.is_active ? "active" : "inactive"
                                                                }`}
                                                        >
                                                            {item.is_active ? "Active" : "Inactive"}
                                                        </span>
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default PaymentTermsList;